import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import { 
  School, Calendar, Bus, Car, Clock, Users, Plus, Edit2, 
  Trash2, Phone, Globe, MapPin, Save, X, CheckCircle,
  AlertCircle, RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import toast from 'react-hot-toast'

interface SchoolInfo {
  id: string
  name: string
  district: string
  type: string
  address: string
  phone: string
  website: string
  calendar_url?: string
}

interface ChildSchoolInfo {
  user_id: string
  username: string
  display_name: string
  school_id: string | null
  school_name: string | null
  transport_mode: string | null
  bus_number: string | null
  school_start_time: string | null
  school_end_time: string | null
  bus_pickup_time: string | null
  bus_dropoff_time: string | null
  car_pickup_time: string | null
  car_dropoff_time: string | null
}

interface SchoolEvent {
  id: string
  school_name: string
  event_date: string
  event_type: string
  name: string
  description?: string
  early_release_time?: string
}

interface PickupRequest {
  id: string
  user_id: string
  username: string
  display_name: string
  request_date: string
  request_time?: string
  request_type: string
  reason: string
  status: string
  created_at: string
}

export const ParentSchoolManagement: React.FC = () => {
  const { user } = useAuth()
  const [children, setChildren] = useState<ChildSchoolInfo[]>([])
  const [schools, setSchools] = useState<SchoolInfo[]>([])
  const [schoolEvents, setSchoolEvents] = useState<Record<string, SchoolEvent[]>>({})
  const [pickupRequests, setPickupRequests] = useState<PickupRequest[]>([])
  const [expandedChild, setExpandedChild] = useState<string | null>(null)
  const [editingChild, setEditingChild] = useState<string | null>(null)
  const [editData, setEditData] = useState<Partial<ChildSchoolInfo>>({})
  const [loading, setLoading] = useState(true)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [selectedSchool, setSelectedSchool] = useState<string>('')
  const [newEvent, setNewEvent] = useState({
    school_id: '',
    name: '',
    event_type: 'other',
    event_date: '',
    description: '',
    early_release_time: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load children data
      const childrenData = await apiClient.get('/api/parent/children')
      const enrichedChildren: ChildSchoolInfo[] = []
      
      for (const child of childrenData) {
        try {
          const schoolInfo = await apiClient.get(`/api/school-transport/school-info/${child.id}`)
          enrichedChildren.push({
            user_id: child.id,
            username: child.username,
            display_name: child.display_name,
            school_id: schoolInfo?.school?.id,
            school_name: schoolInfo?.school?.name,
            transport_mode: schoolInfo?.user?.transport_mode,
            bus_number: schoolInfo?.user?.bus_number,
            school_start_time: schoolInfo?.schedule?.school_start,
            school_end_time: schoolInfo?.schedule?.school_end,
            bus_pickup_time: schoolInfo?.schedule?.bus_pickup,
            bus_dropoff_time: schoolInfo?.schedule?.bus_dropoff,
            car_pickup_time: schoolInfo?.schedule?.car_pickup,
            car_dropoff_time: schoolInfo?.schedule?.car_dropoff
          })
        } catch (error) {
          enrichedChildren.push({
            user_id: child.id,
            username: child.username,
            display_name: child.display_name,
            school_id: null,
            school_name: null,
            transport_mode: null,
            bus_number: null,
            school_start_time: null,
            school_end_time: null,
            bus_pickup_time: null,
            bus_dropoff_time: null,
            car_pickup_time: null,
            car_dropoff_time: null
          })
        }
      }
      
      setChildren(enrichedChildren)
      
      // Load schools
      const schoolsData = await apiClient.get('/api/schools')
      setSchools(schoolsData || [])
      
      // Load events for each school
      const eventsMap: Record<string, SchoolEvent[]> = {}
      for (const school of schoolsData || []) {
        try {
          const events = await apiClient.get(`/api/schools/${school.id}/events`)
          eventsMap[school.id] = events || []
        } catch (error) {
          eventsMap[school.id] = []
        }
      }
      setSchoolEvents(eventsMap)
      
      // Load pickup requests
      const requests = await apiClient.get('/api/school-transport/pickup-requests')
      setPickupRequests(requests?.filter((r: PickupRequest) => r.status === 'pending') || [])
      
    } catch (error) {
      console.error('Failed to load school data:', error)
      toast.error('Failed to load school information')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveChildInfo = async (childId: string) => {
    try {
      await apiClient.put(`/api/school-transport/school-info/${childId}`, editData)
      toast.success('School information updated!')
      setEditingChild(null)
      setEditData({})
      loadData()
    } catch (error) {
      toast.error('Failed to update school information')
    }
  }

  const handleApprovePickup = async (requestId: string) => {
    try {
      await apiClient.put(`/api/school-transport/pickup-requests/${requestId}/approve`)
      toast.success('Pickup request approved!')
      loadData()
    } catch (error) {
      toast.error('Failed to approve pickup request')
    }
  }

  const handleRejectPickup = async (requestId: string) => {
    try {
      await apiClient.put(`/api/school-transport/pickup-requests/${requestId}/reject`)
      toast.success('Pickup request rejected')
      loadData()
    } catch (error) {
      toast.error('Failed to reject pickup request')
    }
  }

  const handleAddEvent = async () => {
    if (!newEvent.school_id || !newEvent.name || !newEvent.event_date) {
      toast.error('Please fill in required fields')
      return
    }

    try {
      await apiClient.post('/api/schools/events', {
        ...newEvent,
        all_day: !newEvent.early_release_time
      })
      toast.success('Event added successfully!')
      setShowAddEvent(false)
      setNewEvent({
        school_id: '',
        name: '',
        event_type: 'other',
        event_date: '',
        description: '',
        early_release_time: ''
      })
      loadData()
    } catch (error) {
      toast.error('Failed to add event')
    }
  }

  const getTransportIcon = (mode: string | null) => {
    switch (mode) {
      case 'bus': return <Bus className="w-4 h-4 text-yellow-600" />
      case 'parent_car': return <Car className="w-4 h-4 text-blue-600" />
      case 'drives_self': return <Car className="w-4 h-4 text-green-600" />
      case 'friend_pickup': return <Users className="w-4 h-4 text-purple-600" />
      default: return null
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2">Loading school information...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <School className="w-6 h-6 mr-2" />
          School Management
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddEvent(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add School Event
          </button>
          <button
            onClick={loadData}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Pending Pickup Requests */}
      {pickupRequests.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-900 mb-3 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            Pending Pickup Requests ({pickupRequests.length})
          </h3>
          <div className="space-y-2">
            {pickupRequests.map(request => (
              <div key={request.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{request.display_name}</div>
                  <div className="text-sm text-gray-600">
                    {format(parseISO(request.request_date), 'EEEE, MMMM d')}
                    {request.request_time && ` at ${request.request_time}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {request.request_type.replace('_', ' ')} - {request.reason}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprovePickup(request.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectPickup(request.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Children's School Information */}
      <div className="space-y-4">
        {children.map(child => (
          <div key={child.user_id} className="bg-white rounded-lg shadow-md">
            <div
              className="p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedChild(expandedChild === child.user_id ? null : child.user_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {expandedChild === child.user_id ? 
                    <ChevronDown className="w-5 h-5 text-gray-500" /> : 
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  }
                  <div>
                    <h3 className="font-semibold text-lg">{child.display_name}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {child.school_name && (
                        <span className="flex items-center">
                          <School className="w-4 h-4 mr-1" />
                          {child.school_name}
                        </span>
                      )}
                      {child.transport_mode && (
                        <span className="flex items-center">
                          {getTransportIcon(child.transport_mode)}
                          <span className="ml-1 capitalize">
                            {child.transport_mode.replace('_', ' ')}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {editingChild !== child.user_id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingChild(child.user_id)
                      setEditData({
                        school_id: child.school_id || '',
                        transport_mode: child.transport_mode || '',
                        bus_number: child.bus_number || '',
                        school_start_time: child.school_start_time || '',
                        school_end_time: child.school_end_time || '',
                        bus_pickup_time: child.bus_pickup_time || '',
                        bus_dropoff_time: child.bus_dropoff_time || '',
                        car_pickup_time: child.car_pickup_time || '',
                        car_dropoff_time: child.car_dropoff_time || ''
                      })
                    }}
                    className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded flex items-center"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </button>
                )}
              </div>
            </div>

            {expandedChild === child.user_id && (
              <div className="border-t p-4">
                {editingChild === child.user_id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">School</label>
                        <select
                          value={editData.school_id || ''}
                          onChange={(e) => setEditData({ ...editData, school_id: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          <option value="">Select a school</option>
                          {schools.map(school => (
                            <option key={school.id} value={school.id}>{school.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Transport Mode</label>
                        <select
                          value={editData.transport_mode || ''}
                          onChange={(e) => setEditData({ ...editData, transport_mode: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          <option value="">Select transport</option>
                          <option value="bus">Bus</option>
                          <option value="parent_car">Parent Car</option>
                          <option value="drives_self">Drives Self</option>
                          <option value="friend_pickup">Friend Pickup</option>
                        </select>
                      </div>
                    </div>

                    {editData.transport_mode === 'bus' && (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Bus Number</label>
                          <input
                            type="text"
                            value={editData.bus_number || ''}
                            onChange={(e) => setEditData({ ...editData, bus_number: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Bus Pickup Time</label>
                          <input
                            type="time"
                            value={editData.bus_pickup_time || ''}
                            onChange={(e) => setEditData({ ...editData, bus_pickup_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Bus Drop-off Time</label>
                          <input
                            type="time"
                            value={editData.bus_dropoff_time || ''}
                            onChange={(e) => setEditData({ ...editData, bus_dropoff_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    )}

                    {(editData.transport_mode === 'parent_car' || editData.transport_mode === 'friend_pickup') && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Car Drop-off Time</label>
                          <input
                            type="time"
                            value={editData.car_pickup_time || ''}
                            onChange={(e) => setEditData({ ...editData, car_pickup_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Car Pickup Time</label>
                          <input
                            type="time"
                            value={editData.car_dropoff_time || ''}
                            onChange={(e) => setEditData({ ...editData, car_dropoff_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">School Start Time</label>
                        <input
                          type="time"
                          value={editData.school_start_time || ''}
                          onChange={(e) => setEditData({ ...editData, school_start_time: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">School End Time</label>
                        <input
                          type="time"
                          value={editData.school_end_time || ''}
                          onChange={(e) => setEditData({ ...editData, school_end_time: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => {
                          setEditingChild(null)
                          setEditData({})
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveChildInfo(child.user_id)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="space-y-4">
                    {/* School Info */}
                    {child.school_name && (
                      <div>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">School Information</h4>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                          <div className="text-sm">
                            <span className="font-medium">School:</span> {child.school_name}
                          </div>
                          {child.school_start_time && (
                            <div className="text-sm">
                              <span className="font-medium">Hours:</span> {child.school_start_time} - {child.school_end_time}
                            </div>
                          )}
                          {schools.find(s => s.id === child.school_id) && (
                            <>
                              {schools.find(s => s.id === child.school_id)?.website && (
                                <a
                                  href={schools.find(s => s.id === child.school_id)?.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline flex items-center"
                                >
                                  <Globe className="w-3 h-3 mr-1" />
                                  School Website
                                </a>
                              )}
                              {schools.find(s => s.id === child.school_id)?.phone && (
                                <div className="text-sm flex items-center">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {schools.find(s => s.id === child.school_id)?.phone}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Transport Info */}
                    {child.transport_mode && (
                      <div>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">Transportation</h4>
                        <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                          <div className="flex items-center text-sm">
                            {getTransportIcon(child.transport_mode)}
                            <span className="ml-2 font-medium capitalize">
                              {child.transport_mode.replace('_', ' ')}
                            </span>
                          </div>
                          {child.bus_number && (
                            <div className="text-sm">
                              <span className="font-medium">Bus Number:</span> {child.bus_number}
                            </div>
                          )}
                          {child.bus_pickup_time && (
                            <div className="text-sm">
                              <span className="font-medium">Bus Times:</span> Pickup {child.bus_pickup_time}, Drop-off {child.bus_dropoff_time}
                            </div>
                          )}
                          {child.car_pickup_time && (
                            <div className="text-sm">
                              <span className="font-medium">Car Times:</span> Drop-off {child.car_pickup_time}, Pickup {child.car_dropoff_time}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Upcoming Events */}
                    {child.school_id && schoolEvents[child.school_id]?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">Upcoming School Events</h4>
                        <div className="space-y-2">
                          {schoolEvents[child.school_id].slice(0, 5).map(event => (
                            <div key={event.id} className="bg-orange-50 rounded-lg p-2">
                              <div className="font-medium text-sm">{event.name}</div>
                              <div className="text-xs text-gray-600">
                                {format(parseISO(event.event_date), 'EEEE, MMMM d')}
                                {event.early_release_time && ` - Early release at ${event.early_release_time}`}
                              </div>
                              {event.description && (
                                <div className="text-xs text-gray-500 mt-1">{event.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add School Event</h3>
            <div className="space-y-4">
              <select
                value={newEvent.school_id}
                onChange={(e) => setNewEvent({ ...newEvent, school_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Select school</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Event name"
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <select
                value={newEvent.event_type}
                onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="other">Other</option>
                <option value="holiday">Holiday</option>
                <option value="early_release">Early Release</option>
                <option value="no_school">No School</option>
                <option value="event">Special Event</option>
              </select>
              <input
                type="date"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <input
                type="time"
                placeholder="Early release time (optional)"
                value={newEvent.early_release_time}
                onChange={(e) => setNewEvent({ ...newEvent, early_release_time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <textarea
                placeholder="Description (optional)"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowAddEvent(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEvent}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}